 
import styled from '@emotion/styled';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Text } from '../atoms/Text';
import { Icon } from '../atoms/Icon';
import { Input } from '@/features/crear-oferta/components/atoms/Input';
import { FormField } from '@/features/crear-oferta/components/molecules/FormField';
import { PhoneInput } from '@/features/crear-oferta/components/molecules/PhoneInput';
import { CounterField } from '@/features/crear-oferta/components/molecules/CounterField';
import { RadioGroup } from '@/features/crear-oferta/components/molecules/RadioGroup';
import { Select } from '../atoms/Select';
import { TIPO_GRUPO_OPTIONS, FINANCIERA_OPTIONS, HISTORIAL_CREDITICIO_OPTIONS } from '@/features/crear-oferta/constants/formOptions';
import {
  sanitizeNombre,
  sanitizeMonto,
  numberToMonto,
  validateNombreCompleto,
  validateTelefono,
  validateTamanoGrupo,
  validateMontoGrupal,
  validateFinanciera,
  validateCiclos,
} from '@/features/crear-oferta/utils';

export interface ClienteInfoProps {
  nombreCompleto: string;
  telefono: {
    codigo: string;
    numero: string;
  };
  tamanoGrupo: number;
  montoGrupal: number;
  ciclo: number;
  financiera?: string;
  tipoGrupo: 'mujeres' | 'mixto';
  disabled?: boolean;
  onChange?: (data: {
    nombreCompleto?: string;
    telefono?: { codigo: string; numero: string };
    tamanoGrupo?: number;
    montoGrupal?: number;
    ciclo?: number;
    financiera?: string;
    tipoGrupo?: 'mujeres' | 'mixto';
  }) => void;
  onValidationChange?: (isValid: boolean) => void;
  className?: string;
}

interface ClienteInfoErrors {
  nombreCompleto?: string;
  telefono?: string;
  tamanoGrupo?: string;
  montoGrupal?: string;
  financiera?: string;
  ciclosCompletados?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(3.25)};
  width: 100%;
`;

const SectionTitle = styled(Text)`
  font-size: ${(props) => props.theme.typography.body3Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body3Bold.fontWeight};
  line-height: ${(props) => props.theme.typography.body3Bold.lineHeight};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing(3.25)};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  /* Tablet: 2 columnas */
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${(props) => props.theme.spacing(3)} ${(props) => props.theme.spacing(4)};
  }

  /* Desktop: 3 columnas */
  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${(props) => props.theme.spacing(3)} ${(props) => props.theme.spacing(6)};
  }

  /* Large Desktop: 4 columnas */
  @media (min-width: 1600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: ${(props) => props.theme.spacing(3)} ${(props) => props.theme.spacing(6)};
  }
`;

const FullWidthItem = styled.div`
  @media (min-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const ErrorMessage = styled.span`
  display: block;
  margin-top: ${(props) => props.theme.spacing(0.5)};
  color: ${(props) => props.theme.colors.state.alert[1000]};
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: ${(props) => props.theme.typography.captionRegular.fontSize};
  font-weight: ${(props) => props.theme.typography.captionRegular.fontWeight};
`;

export const ClienteInfo = ({
  nombreCompleto,
  telefono,
  tamanoGrupo,
  montoGrupal,
  ciclo,
  financiera,
  tipoGrupo,
  disabled = false,
  onChange,
  onValidationChange,
  className,
}: ClienteInfoProps) => {
  // Local state for editable fields
  const [localNombreCompleto, setLocalNombreCompleto] = useState(nombreCompleto);
  const [localTelefono, setLocalTelefono] = useState(telefono);
  const [localTamanoGrupo, setLocalTamanoGrupo] = useState(tamanoGrupo);
  const [localMontoGrupal, setLocalMontoGrupal] = useState(montoGrupal);
  const [localCiclo, setLocalCiclo] = useState(ciclo);
  const [localFinanciera, setLocalFinanciera] = useState(financiera);
  const [localTipoGrupo, setLocalTipoGrupo] = useState(tipoGrupo);

  // Historial crediticio as independent state (derived from initial ciclo)
  const [localHistorialCrediticio, setLocalHistorialCrediticio] = useState<'sin' | 'con'>(
    ciclo === 1 ? 'sin' : 'con'
  );

  // Validation errors - computed from local state

  // Track previous props to detect changes and update state outside of effect
  const prevPropsRef = useRef({
    nombreCompleto,
    telefono,
    tamanoGrupo,
    montoGrupal,
    ciclo,
    financiera,
    tipoGrupo,
  });

  // Update local state when props change
  // Using a pattern that avoids setState in effect by using a callback ref pattern
  useEffect(() => {
    const prev = prevPropsRef.current;
    const hasChanged =
      prev.nombreCompleto !== nombreCompleto ||
      prev.telefono !== telefono ||
      prev.tamanoGrupo !== tamanoGrupo ||
      prev.montoGrupal !== montoGrupal ||
      prev.ciclo !== ciclo ||
      prev.financiera !== financiera ||
      prev.tipoGrupo !== tipoGrupo;

    if (hasChanged) {
      // Schedule state updates in next tick to avoid setState in effect
      queueMicrotask(() => {
        setLocalNombreCompleto(nombreCompleto);
        setLocalTelefono(telefono);
        setLocalTamanoGrupo(tamanoGrupo);
        setLocalMontoGrupal(montoGrupal);
        setLocalCiclo(ciclo);
        setLocalFinanciera(financiera);
        setLocalTipoGrupo(tipoGrupo);
        setLocalHistorialCrediticio(ciclo === 1 ? 'sin' : 'con');
      });
      prevPropsRef.current = {
        nombreCompleto,
        telefono,
        tamanoGrupo,
        montoGrupal,
        ciclo,
        financiera,
        tipoGrupo,
      };
    }
  }, [nombreCompleto, telefono, tamanoGrupo, montoGrupal, ciclo, financiera, tipoGrupo]);

  // Handlers
  const handleNombreCompletoChange = (value: string) => {
    const cleanValue = sanitizeNombre(value);
    setLocalNombreCompleto(cleanValue);
    onChange?.({ nombreCompleto: cleanValue });
  };

  const handleTelefonoChange = (numero: string) => {
    const newTelefono = { ...localTelefono, numero };
    setLocalTelefono(newTelefono);
    onChange?.({ telefono: newTelefono });
  };

  const handleTamanoGrupoChange = (value: number) => {
    setLocalTamanoGrupo(value);
    onChange?.({ tamanoGrupo: value });
  };

  const handleMontoGrupalChange = (value: string) => {
    const formattedValue = sanitizeMonto(value);

    // Convertir el valor formateado a número
    const digitsOnly = formattedValue.replace(/,/g, '');
    const numericValue = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);

    setLocalMontoGrupal(numericValue);
    onChange?.({ montoGrupal: numericValue });
  };

  const handleHistorialCrediticioChange = (value: 'sin' | 'con') => {
    setLocalHistorialCrediticio(value);

    // Si selecciona "sin historial", ciclo = 1 y limpiar financiera
    if (value === 'sin') {
      setLocalCiclo(1);
      setLocalFinanciera(undefined);
      onChange?.({ ciclo: 1, financiera: undefined });
    } else {
      // Si selecciona "con historial" y ciclo es 1, cambiar a 2
      if (localCiclo === 1) {
        setLocalCiclo(2);
        onChange?.({ ciclo: 2 });
      }
    }
  };

  const handleCicloChange = (value: number) => {
    setLocalCiclo(value);
    onChange?.({ ciclo: value });
  };

  const handleFinancieraChange = (value: string) => {
    setLocalFinanciera(value);
    onChange?.({ financiera: value });
  };

  const handleTipoGrupoChange = (value: 'mujeres' | 'mixto') => {
    setLocalTipoGrupo(value);
    onChange?.({ tipoGrupo: value });
  };

  // Validation function (same as CreateOfferForm)
  const validateFields = useCallback((): ClienteInfoErrors => {
    const newErrors: ClienteInfoErrors = {};
    const tieneHistorial = localHistorialCrediticio === 'con';

    // Validar nombre completo
    const nombreError = validateNombreCompleto(localNombreCompleto);
    if (nombreError) {
      newErrors.nombreCompleto = nombreError;
    }

    // Validar teléfono
    const telefonoError = validateTelefono(localTelefono.numero);
    if (telefonoError) {
      newErrors.telefono = telefonoError;
    }

    // Validar tamaño del grupo
    const tamanoError = validateTamanoGrupo(localTamanoGrupo);
    if (tamanoError) {
      newErrors.tamanoGrupo = tamanoError;
    }

    // Validar monto grupal (convertir número a formato string para validación)
    const montoFormateado = numberToMonto(localMontoGrupal);
    const montoError = validateMontoGrupal(montoFormateado);
    if (montoError) {
      newErrors.montoGrupal = montoError;
    }

    // Validar financiera si tiene historial
    const financieraError = validateFinanciera(localFinanciera, tieneHistorial);
    if (financieraError) {
      newErrors.financiera = financieraError;
    }

    // Validar ciclos completados si tiene historial
    const ciclosError = validateCiclos(localCiclo, tieneHistorial);
    if (ciclosError) {
      newErrors.ciclosCompletados = ciclosError;
    }

    return newErrors;
  }, [
    localNombreCompleto,
    localTelefono,
    localTamanoGrupo,
    localMontoGrupal,
    localCiclo,
    localFinanciera,
    localHistorialCrediticio,
  ]);

  // Compute validation errors using useMemo
  const errors = useMemo(() => validateFields(), [validateFields]);
  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  // Notify parent when validation changes
  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  // Format monto grupal para mostrar
  const montoFormateado = numberToMonto(localMontoGrupal);

  return (
    <Container className={className}>
      <FullWidthItem>
        <SectionTitle variant="body3" weight="bold">
          Datos del cliente
        </SectionTitle>
      </FullWidthItem>

      <FormGrid>
        <FormField label="Nombre completo" required>
          <Input
            value={localNombreCompleto}
            onChange={handleNombreCompletoChange}
            placeholder="Ingresa nombre completo"
            suffix={<Icon name="user" color="white" size={24} />}
            disabled={disabled}
          />
          {errors.nombreCompleto && <ErrorMessage>{errors.nombreCompleto}</ErrorMessage>}
        </FormField>

        <FormField label="Teléfono móvil" required>
          <PhoneInput
            countryCode={localTelefono.codigo}
            phoneNumber={localTelefono.numero}
            onPhoneNumberChange={handleTelefonoChange}
            disabled={disabled}
          />
          {errors.telefono && <ErrorMessage>{errors.telefono}</ErrorMessage>}
        </FormField>

        <CounterField
          label="Tamaño del grupo"
          value={localTamanoGrupo}
          onChange={handleTamanoGrupoChange}
          min={5}
          max={50}
          required
          disabled={disabled}
          error={errors.tamanoGrupo}
        />

        <FormField label="Monto grupal MXN" required>
          <Input
            value={montoFormateado}
            onChange={handleMontoGrupalChange}
            placeholder="ej. 11,000"
            prefix="$"
            disabled={disabled}
          />
          {errors.montoGrupal && <ErrorMessage>{errors.montoGrupal}</ErrorMessage>}
        </FormField>

        <FullWidthItem>
          <RadioGroup<'sin' | 'con'>
            label="Tiene historial crediticio"
            name="historialCrediticio"
            options={HISTORIAL_CREDITICIO_OPTIONS}
            value={localHistorialCrediticio}
            onChange={handleHistorialCrediticioChange}
            required
            disabled={disabled}
          />
        </FullWidthItem>

        {localHistorialCrediticio === 'con' && (
          <>
            <FormField label="Con que financiera">
              <Select
                value={localFinanciera || ''}
                onChange={handleFinancieraChange}
                options={FINANCIERA_OPTIONS}
                placeholder="Selecciona una opción"
                disabled={disabled}
              />
              {errors.financiera && <ErrorMessage>{errors.financiera}</ErrorMessage>}
            </FormField>

            <CounterField
              label="Ciclos completados"
              value={localCiclo}
              onChange={handleCicloChange}
              min={1}
              max={99}
              required
              disabled={disabled}
              error={errors.ciclosCompletados}
            />
          </>
        )}

        <FullWidthItem>
          <RadioGroup<'mujeres' | 'mixto'>
            label="Tipo de grupo"
            name="tipoGrupo"
            options={TIPO_GRUPO_OPTIONS}
            value={localTipoGrupo}
            onChange={handleTipoGrupoChange}
            required
            disabled={disabled}
          />
        </FullWidthItem>
      </FormGrid>
    </Container>
  );
};
